import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, LayoutDashboard, User } from 'lucide-react';
import { toast } from 'sonner';
import { clientApi } from '../api';
import { useBranding } from '../context/BrandingContext';
import BrandLogo from '../components/BrandLogo';

const Landing = () => {
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('choose');
  const [busy, setBusy] = useState(false);
  const { branding } = useBranding();
  const videoUrl = branding?.hero_video_url;

  const goClient = async (e) => {
    e && e.preventDefault();
    if (!name.trim()) return toast.error('Inserisci il tuo nome');
    if (!password) return toast.error('Inserisci la password');
    setBusy(true);
    try {
      const data = await clientApi.unlockGlobal({ password, display_name: name.trim() });
      toast.success(`Benvenuto/a ${name.trim()}`);
      nav(`/c/${data.slug}/home`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Password errata');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white relative overflow-hidden flex flex-col">
      {/* Animated video background */}
      {videoUrl && (
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-screen pointer-events-none"
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
        />
      )}
      {/* Gold ambient blur (subtle) */}
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-[#C9A961]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#C9A961]/5 rounded-full blur-3xl" />
      {/* Dark gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80 pointer-events-none" />

      <header className="relative z-10 px-6 py-6 max-w-7xl mx-auto w-full">
        <BrandLogo size="md" />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-2xl text-center fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 backdrop-blur border border-[#C9A961]/30 text-[#C9A961] text-[11px] uppercase tracking-[0.25em] mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961]" /> Consegna foto professionale
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.05] mb-6" style={{ textShadow: '0 4px 24px rgba(0,0,0,0.6)' }}>
            Le tue foto,<br /><span className="italic text-[#C9A961]">consegnate con cura.</span>
          </h1>
          <p className="text-slate-300 text-lg mb-12 max-w-xl mx-auto" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
            Accedi alla tua area riservata per vedere, commentare e scaricare le foto del tuo shooting.
          </p>

          {mode === 'choose' ? (
            <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto">
              <button onClick={() => setMode('client-code')} className="group relative bg-[#C9A961] hover:bg-white text-black rounded-2xl p-6 text-left transition-colors shadow-2xl">
                <User className="w-6 h-6 mb-3" />
                <div className="font-semibold text-lg">Area cliente</div>
                <div className="text-xs opacity-80 mt-1">Accedi al tuo lavoro con la password ricevuta</div>
                <ArrowRight className="w-4 h-4 absolute bottom-6 right-6 group-hover:translate-x-1 transition" />
              </button>
              <Link to="/admin/login" className="group relative bg-black/50 backdrop-blur hover:bg-black/70 border border-white/10 hover:border-[#C9A961]/40 rounded-2xl p-6 text-left transition-all shadow-2xl">
                <LayoutDashboard className="w-6 h-6 mb-3 text-[#C9A961]" />
                <div className="font-semibold text-lg text-white">Admin</div>
                <div className="text-xs text-slate-400 mt-1">Accesso riservato a Nicola Verardo</div>
                <ArrowRight className="w-4 h-4 absolute bottom-6 right-6 text-[#C9A961] group-hover:translate-x-1 transition" />
              </Link>
            </div>
          ) : (
            <form onSubmit={goClient} className="max-w-md mx-auto bg-black/60 backdrop-blur border border-white/10 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-[0.2em] mb-3 justify-center">
                <Lock className="w-3.5 h-3.5" /> Accesso cliente
              </div>
              <label className="block mb-3 text-left">
                <span className="text-xs text-slate-300">Il tuo nome</span>
                <input data-testid="landing-name-input" value={name} onChange={e=>setName(e.target.value)} autoFocus autoComplete="off" placeholder="Es. Mario o la tua email" className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#C9A961] text-white placeholder:text-slate-500" />
              </label>
              <label className="block mb-4 text-left">
                <span className="text-xs text-slate-300">Password</span>
                <input data-testid="landing-password-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} autoComplete="new-password" placeholder="La password che ti ha inviato Nicola" className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#C9A961] text-white placeholder:text-slate-500" />
              </label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={()=>setMode('choose')} className="px-4 py-2.5 text-sm text-slate-400 hover:text-white transition">Indietro</button>
                <button type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-white text-black font-semibold px-4 py-2.5 rounded-full transition">
                  {busy ? 'Verifica...' : 'Continua'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <footer className="relative z-10 px-6 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} FRAMEBOX by Nicola Verardo
      </footer>
    </div>
  );
};

export default Landing;

